/* eslint-disable eqeqeq */
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import cx from 'classnames';
import {
  SvgIcon,
  AnalyzeItem,
  Loader,
  ModalInfo,
  ModalInfoProps,
  AlertBox,
  ConfirmActionModal,
} from '@/components';
import { ExploreConnectionRequest, TransactionDirectionEnum } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { InvestigationHistoryItemData } from '@/redux/models';
import {
  investigationHistorySelectors,
  serviceOptionsSelectors,
} from '@/redux/selectors';
import { investigationHistoryActions } from '@/redux/actions';
import lockBgImage from '@/assets/images/analyze/bg-analyze-blur.jpg';
import { ChainsDictionary } from '@/config';
import { useModal } from '@/hooks';

import styles from './AnalysisInfoModal.module.sass';

interface ItemBlockProps {
  connection: Record<string, any>;
  entry: Record<string, any>;
  item: InvestigationHistoryItemData | null;
  index: number;
}

const Item: FC<ItemBlockProps> = (props) => {
  const { connection, entry, item, index } = props;
  const field =
    connection.direction === TransactionDirectionEnum.SENT
      ? 'target'
      : 'source';
  const interactor = entry[0][field];
  const tx = entry[0].txs[0];
  const title = useMemo(() => {
    if (!interactor) {
      return 'unknown';
    }

    if (interactor.type === 'cluster') {
      return 'unknown cluster';
    }

    if (interactor.type === 'entity') {
      if (index === 0) {
        return connection.title;
      }

      return connection.title;
    }

    if (interactor.type === 'address' && interactor.address) {
      if (interactor.address === item?.address) {
        return connection.title || 'unknown';
      }
      return interactor.address;
    }

    return 'unknown';
  }, [entry]);

  const txHash = useMemo(() => {
    if (tx.root_hash) {
      return tx.root_hash;
    }

    if (tx.hash) {
      return tx.hash;
    }
  }, [entry]);

  return (
    <div className={styles.item}>
      <div className={styles.item__title}>{title}</div>
      <div className={styles.item__value}>{txHash}</div>
    </div>
  );
};

interface AnalysisInfoModalProps extends ModalInfoProps {
  item: InvestigationHistoryItemData | null;
  connection: Record<string, any>;
}

interface InfoBlockProps {
  isDanger?: boolean;
  value: string;
  caption: string;
}

interface HeaderData {
  title: string;
  description: string;
}

const Info: FC<InfoBlockProps> = ({ isDanger, value, caption }) => {
  const classes = cx(styles.info, {
    [styles['is-danger']]: isDanger,
  });

  const displayAmount = (isDanger ? '-' : '+') + value;

  return (
    <div className={classes}>
      <div className={styles.info__value}>
        <span>{displayAmount}</span>
      </div>
      <div className={styles.info__caption}>
        <span>{caption}</span>
      </div>
    </div>
  );
};

const AnalysisInfoModal: FC<AnalysisInfoModalProps> = (props) => {
  const { isOpen, onClose, item, connection } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: serviceOptionData } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );
  const checkCost = 2 * (Number(serviceOptionData?.expandedCheckCost) || 5);
  const {
    isOpen: isOpenConfirmModal,
    onOpenModal: handleOpenConfirmModal,
    onCloseModal: handleCloseConfirmModal,
  } = useModal();

  const [leftHeader, setLeftHeader] = useState<HeaderData>({
    title: '',
    description: '',
  });
  const [rightHeader, setRightHeader] = useState<HeaderData>({
    title: '',
    description: '',
  });

  const isReceived = useMemo(
    () => connection.direction === TransactionDirectionEnum.RECEIVED,
    [connection],
  );

  useEffect(() => {
    if (isReceived) {
      setLeftHeader({
        title: connection.title,
        description: connection.description,
      });
      setRightHeader({
        title: item?.address || '',
        description:
          t('translation.inv_cryptowallet_str', {
            asset: ChainsDictionary[item?.asset || ''].name || item?.network,
          }) || '',
      });
    } else {
      setLeftHeader({
        title: item?.address || '',
        description:
          t('translation.inv_cryptowallet_str', {
            asset: ChainsDictionary[item?.asset || ''].name || item?.network,
          }) || '',
      });
      setRightHeader({
        title: connection.title,
        description: connection.description,
      });
    }
  }, [isReceived, connection, item, t]);

  const { loading, error } = useAppSelector(
    investigationHistorySelectors.getInvestigationHistorySlice,
  );

  useEffect(() => {
    return () => {
      dispatch(investigationHistoryActions.setError(null));
    };
  }, []);

  const exploreConnectionHandler = () => {
    handleOpenConfirmModal();
  };

  const exploreConnection = () => {
    const payload: ExploreConnectionRequest = {
      address: item?.address || '',
      identifier: item?.identifier || '',
      target: connection.entity?.id,
      type: connection.direction,
      asset: item?.asset,
    };
    dispatch(investigationHistoryActions.exploreConnection(payload));
    handleCloseConfirmModal();
  };

  const confirmMessage = useMemo(() => {
    return parse(
      t('translation.connection_extra_funds_required', {
        count: checkCost,
      }).replaceAll('\n', '<br />'),
    );
  }, []);

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.investigation_modal_title')}
      className={styles.modal}
      onClose={onClose}
    >
      {!!error && (
        <AlertBox className={styles.error} type="danger">
          {{ description: error }}
        </AlertBox>
      )}

      <div className={styles.summary}>
        <div className={styles.col}>
          <AnalyzeItem
            title={leftHeader.title}
            description={leftHeader.description}
            isSelected={!isReceived}
            className={styles.analyzeItem}
          />
        </div>

        <div className={styles.col}>
          <Info
            value={connection.value}
            caption={connection.caption}
            isDanger={!isReceived}
          />
        </div>

        <div className={styles.col}>
          <AnalyzeItem
            title={rightHeader.title}
            description={rightHeader.description}
            isSelected={isReceived}
            className={styles.analyzeItem}
          />
        </div>
      </div>
      {loading && <Loader />}

      {!loading && (
        <>
          {!connection.path && (
            <div
              className={styles.lock}
              style={{ backgroundImage: `url(${lockBgImage})` }}
            >
              <button
                type="button"
                className={styles.unlockButton}
                onClick={exploreConnectionHandler}
              >
                <SvgIcon name="aml" className={styles.unlockButton__icon} />
                <span>{checkCost}</span>
                {t('translation.investigation_modal_btn')}
              </button>
            </div>
          )}

          {connection.path && (
            <div className={styles.list}>
              <div className={styles.list__scroll}>
                <div className={styles.list__items}>
                  {connection.path && (
                    <>
                      {connection.direction ===
                        TransactionDirectionEnum.SENT && (
                        <div className={styles.item}>
                          {item?.baseData?.entity && (
                            <div className={styles.item__title}>
                              <span>
                                {item?.baseData?.entity.name ||
                                  item?.baseData?.entity.slug ||
                                  'unknown'}
                              </span>
                            </div>
                          )}

                          <div className={styles.item__value}>
                            {item?.address}
                          </div>
                        </div>
                      )}

                      {connection.path.map((entry: any, index: number) => (
                        <Item
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          connection={connection}
                          entry={entry}
                          item={item}
                          index={index}
                        />
                      ))}

                      {connection.direction ===
                        TransactionDirectionEnum.RECEIVED && (
                        <div className={styles.item}>
                          {item?.baseData?.entity && (
                            <div className={styles.item__title}>
                              <span>
                                {item?.baseData?.entity.name ||
                                  item?.baseData?.entity.slug ||
                                  'unknown'}
                              </span>
                            </div>
                          )}

                          <div className={styles.item__value}>
                            {item?.address}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <ConfirmActionModal
        title={t('translation.alert_warning_title')}
        isOpen={isOpenConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={exploreConnection}
        confirmButtonText={t('translation.confirm')}
        cancelButtonText={t('translation.cancel_btn')}
      >
        {confirmMessage}
      </ConfirmActionModal>
    </ModalInfo>
  );
};

export default AnalysisInfoModal;
